import { useEffect, useState } from 'react';

/*
 * "Hide PHI" — a privacy screen for screen-sharing, demos and screenshots.
 *
 * Turning it on puts `pm-phi-hidden` on <html>; ui.css then blurs every
 * element marked `data-phi` (names, phones, emails, DOBs, addresses,
 * patient / chart / member IDs, free-text notes). The data itself is never
 * altered — nothing masked can be typed into a form or written back to the
 * PMS — and it is NOT access control: the real values are still in the page.
 *
 * The class lives on <html> rather than the dashboard root so modals and
 * popovers positioned outside it are covered too. The choice is remembered
 * per browser and follows across open tabs.
 */
export const PHI_HIDDEN_CLASS = 'pm-phi-hidden';
const STORAGE_KEY = 'pmHidePhi';

const readStored = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

const applyClass = (hidden) => {
  document.documentElement.classList.toggle(PHI_HIDDEN_CLASS, hidden);
};

export function useHidePhi() {
  const [hidden, setHidden] = useState(readStored);

  useEffect(() => {
    applyClass(hidden);
    try {
      window.localStorage.setItem(STORAGE_KEY, hidden ? '1' : '0');
    } catch {
      /* private mode etc. — the toggle still works for this page */
    }
  }, [hidden]);

  // Keep other open dashboard tabs in step.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setHidden(e.newValue === '1');
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Leaving the dashboard (log out → login page) drops the blur class.
  useEffect(() => () => applyClass(false), []);

  return [hidden, setHidden];
}
